import { supabase } from "@/integrations/supabase/client";

/**
 * Resolved per-court availability for an event.
 *
 * `event_sessions.court_id IS NULL` rows are the EVENT DEFAULT — they apply to
 * every court. A row with `court_id = <court>` is an OVERRIDE for that one
 * court on that one date.
 *
 * Resolution rule (implement/consume exactly this):
 *   For court C on date D — if any active rows exist with court_id = C and
 *   session_date = D, use those and ignore the defaults for that date.
 *   Otherwise use the rows with court_id IS NULL for date D.
 *
 * Override is per DATE, not per event: a court can have custom hours on
 * Saturday and default hours on Sunday.
 *
 * Everything downstream should call `getResolvedAvailability` rather than
 * reading `event_sessions` directly.
 */

export interface AvailabilityWindow {
  session_id: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  label: string | null;
  /** true when this window came from a court-scoped override row */
  is_override: boolean;
}

export interface ResolvedCourtAvailability {
  court_id: string;
  court_name: string;
  /** date -> windows for that date */
  by_date: Record<string, AvailabilityWindow[]>;
  /** dates on which this court uses court-scoped overrides */
  override_dates: string[];
}

export interface ResolvedAvailability {
  dates: string[];
  courts: ResolvedCourtAvailability[];
  /** the event-wide default windows, grouped by date */
  defaults_by_date: Record<string, AvailabilityWindow[]>;
}

export interface RawSession {
  id: string;
  court_id: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  label?: string | null;
  is_active?: boolean | null;
  sort_order?: number | null;
}

export interface RawCourt {
  id: string;
  name: string;
  is_active?: boolean | null;
  sort_order?: number | null;
}

const toWindow = (s: RawSession): AvailabilityWindow => ({
  session_id: s.id,
  session_date: s.session_date,
  start_time: s.start_time,
  end_time: s.end_time,
  label: s.label ?? null,
  is_override: s.court_id !== null,
});

/** Pure resolver — takes already-loaded rows so it can be unit tested / reused. */
export function resolveAvailability(
  courts: RawCourt[],
  sessions: RawSession[]
): ResolvedAvailability {
  const activeCourts = courts.filter((c) => c.is_active !== false);
  const activeSessions = sessions.filter((s) => s.is_active !== false);

  const defaults_by_date: Record<string, AvailabilityWindow[]> = {};
  const overrides: Record<string, Record<string, AvailabilityWindow[]>> = {};

  for (const s of activeSessions) {
    const w = toWindow(s);
    if (s.court_id === null) {
      (defaults_by_date[s.session_date] ||= []).push(w);
    } else {
      const perCourt = (overrides[s.court_id] ||= {});
      (perCourt[s.session_date] ||= []).push(w);
    }
  }

  const dates = Array.from(
    new Set([
      ...Object.keys(defaults_by_date),
      ...Object.values(overrides).flatMap((m) => Object.keys(m)),
    ])
  ).sort();

  const resolvedCourts: ResolvedCourtAvailability[] = activeCourts.map((c) => {
    const courtOverrides = overrides[c.id] ?? {};
    const by_date: Record<string, AvailabilityWindow[]> = {};
    for (const d of dates) {
      const scoped = courtOverrides[d];
      const windows = scoped && scoped.length > 0 ? scoped : defaults_by_date[d] ?? [];
      if (windows.length > 0) by_date[d] = windows;
    }
    return {
      court_id: c.id,
      court_name: c.name,
      by_date,
      override_dates: Object.keys(courtOverrides)
        .filter((d) => (courtOverrides[d] ?? []).length > 0)
        .sort(),
    };
  });

  return { dates, courts: resolvedCourts, defaults_by_date };
}

/**
 * Load resolved availability for an event.
 *
 * Reads the SAME Postgres function the placer uses —
 * `rle_resolved_availability(p_event_id)` — so the capacity preview and
 * `assign_slots` can never disagree about a court's open hours.
 */
export async function getResolvedAvailability(
  eventId: string
): Promise<ResolvedAvailability> {
  const [courtsRes, rowsRes] = await Promise.all([
    (supabase as any)
      .from("event_courts")
      .select("id, name, is_active, sort_order")
      .eq("event_id", eventId)
      .order("sort_order", { ascending: true }),
    (supabase as any).rpc("rle_resolved_availability", { p_event_id: eventId }),
  ]);

  if (courtsRes.error) throw courtsRes.error;
  if (rowsRes.error) throw rowsRes.error;

  const courts: RawCourt[] = courtsRes.data ?? [];
  const nameById = new Map(courts.map((c) => [c.id, c.name]));
  const rows: Array<{
    court_id: string;
    session_id: string | null;
    session_date: string;
    start_time: string;
    end_time: string;
  }> = rowsRes.data ?? [];

  const byCourt = new Map<string, Record<string, AvailabilityWindow[]>>();
  for (const r of rows) {
    const m = byCourt.get(r.court_id) ?? {};
    (m[r.session_date] ||= []).push({
      session_id: r.session_id,
      session_date: r.session_date,
      start_time: r.start_time,
      end_time: r.end_time,
      label: null,
      is_override: false,
    });
    byCourt.set(r.court_id, m);
  }

  const dates = Array.from(new Set(rows.map((r) => r.session_date))).sort();

  return {
    dates,
    courts: courts
      .filter((c) => c.is_active !== false)
      .map((c) => ({
        court_id: c.id,
        court_name: nameById.get(c.id) ?? "",
        by_date: byCourt.get(c.id) ?? {},
        override_dates: [],
      })),
    defaults_by_date: {},
  };
}


/** Minutes between two "HH:MM(:SS)" strings. */
export function windowMinutes(start: string, end: string): number {
  const p = (t: string) => {
    const [h, m] = String(t).split(":");
    return Number(h) * 60 + Number(m);
  };
  return Math.max(0, p(end) - p(start));
}

export interface CapacityDay {
  date: string;
  courts: number;
  minutes: number;
  slots: number;
}

/**
 * Slots per date. Rounds DOWN per court per window; never rounds across
 * windows. Returns null when the slot increment is not usable.
 */
export function computeCapacity(
  resolved: ResolvedAvailability,
  slotMinutes: number | null
): CapacityDay[] | null {
  if (!slotMinutes || slotMinutes <= 0) return null;

  return resolved.dates.map((date) => {
    let slots = 0;
    let minutes = 0;
    let courts = 0;
    for (const c of resolved.courts) {
      const windows = c.by_date[date] ?? [];
      if (windows.length === 0) continue;
      courts += 1;
      for (const w of windows) {
        const mins = windowMinutes(w.start_time, w.end_time);
        minutes += mins;
        slots += Math.floor(mins / slotMinutes);
      }
    }
    return { date, courts, minutes, slots };
  });
}
